import { Avatar } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { revertPositionTitleV2, stringAvatar } from 'shared-utils';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import {
	CompanyDocument,
	OfflineDriveCandidateStatus,
	OfflineDrivePositions,
	SkillsTypeForDrivePositions,
} from 'types';
import DisplayRoom from '../../DisplayRoom';
import CompaniesActions from '../../offlineDriveCompaniesList/CompaniesActions';
import { useUserContext } from 'src/context/UserProvider';
import { Tooltip } from '@mui/material';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { FaCheckCircle } from 'react-icons/fa';
import dayjs from 'dayjs';

interface Props {
	position: OfflineDrivePositions;
	bulkSelect: boolean;
	attendDriveDate?: Date;
}

const MobileCardForOfflineDrivePosition = ({ position, bulkSelect, attendDriveDate }: Props) => {
	const { selectedDrive, bulkSelectIds, setBulkSelectIds } = useUserContext();
	const [avatarImg, setAvatarImg] = useState('');
	const isCompanyPresent: boolean =
		position?.company?.offlineDriveCompanyStatus === OfflineDriveCandidateStatus.PRESENT ||
		position?.company?.driveInvitees?.offlineDriveCandidateStatus === OfflineDriveCandidateStatus.PRESENT;
	const generateAvatar = async (data: CompanyDocument) => {
		const res = await generateCompanyLogoAvatarSrc(data?.logo || '', data?.url || '');
		setAvatarImg(res);
	};
	const sortedSkillsRequired = useMemo(() => {
		return position?.skills.sort((a, b) => a.name.length - b.name.length);
	}, [position?.skills]);

	const isSelected = bulkSelectIds.find(bulkPosition => bulkPosition?.toString() === position?._id?.toString())
		? true
		: false;

	useEffect(() => {
		generateAvatar({ logo: position?.company?.logo, url: position?.company?.url } as CompanyDocument);
	}, [position?.company?.name, position?.company?.url]);
	return (
		<div
			className={`w-full border border-[#EBEBF0] rounded-[12px] bg-[#FFF] shadow-[0px_1px_2px_rgba(226,226,226,0.00),_0px_1px_3px_rgba(167,167,167,0.09)] flex flex-col box-border pt-4 relative ${
				bulkSelect ? 'cursor-pointer' : 'cursor-auto'
			}`}
			onClick={() => {
				if (bulkSelect) {
					if (isSelected) {
						setBulkSelectIds(prev =>
							prev.filter(bulkCandidate => bulkCandidate?.toString() !== position?._id?.toString()),
						);
						return;
					}
					setBulkSelectIds(prev => [...prev, position?._id?.toString()]);
				}
			}}
		>
			{bulkSelect ? (
				<FaRegCircleCheck
					className={`w-[24px] h-[24px] absolute right-[-7px] pointer-events-none ${
						isSelected ? 'bg-[#7777E7] text-[#CCDDFF]' : 'bg-white text-[#555770]'
					} rounded-full top-[-7px] `}
				/>
			) : null}
			<div className="flex justify-between px-4">
				<div className="flex flex-1 min-w-[80px] relative">
					<div className="flex items-start gap-2">
						<Avatar
							rounded
							size="md"
							className="text-xl"
							img={avatarImg}
							placeholderInitials={stringAvatar(position?.company?.name?.trim()?.toUpperCase() || '')}
						/>

						<div className="flex flex-col">
							<div className="flex gap-1 items-center">
								<p className="text-[#1C1C28] font-inter text-base font-medium">{position?.company?.name}</p>
								{isCompanyPresent ? (
									<span>
										<FaCheckCircle className="text-blue-500" />
									</span>
								) : null}
							</div>
							<div className="flex">
								<p className="text-[#28293D] font-inter text-xs font-medium mt-[4px]">
									{revertPositionTitleV2(position?.title)}
									{position?.positionLabel ? ` (${position?.positionLabel})` : ''}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* middle */}

			<div className="py-2.5 flex flex-col gap-2.5 px-4">
				<div className="flex justify-between">
					{/* 1 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Salary Offering</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<p className="font-inter text-xs font-medium ">{`${position?.salary?.min}-${position?.salary?.max}LPA`}</p>
						</div>
					</div>
					{/* 2 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Skills Required</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<>
								<div className="flex text-center text-xs">
									{sortedSkillsRequired.slice(0, 2).map((skill, index) => (
										<span key={index}>
											{skill.name}
											{index < 2 ? ', ' : ''}
										</span>
									))}
									{sortedSkillsRequired.length > 2 && (
										<Tooltip
											title={
												<div className="text-center">
													{position?.skills

														.slice(2)
														.map((skill: SkillsTypeForDrivePositions) => skill.name)
														.join(', ')}
												</div>
											}
											arrow
											placement="bottom"
										>
											<span className="flex border-b border-[#555770] border-dashed w-fit  text-xs font-[500] text-[#555770]">
												more
											</span>
										</Tooltip>
									)}
								</div>
							</>
						</div>
					</div>
				</div>
				<div className="flex justify-between">
					{/* 1 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Openings</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<p className="font-inter text-xs font-medium ">{position?.vacancy}</p>
						</div>
					</div>
					{/* 2 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Shortlisted</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<p className="font-inter text-xs font-medium ">{position?.shortlistedCount}</p>
						</div>
					</div>
				</div>
				<div className="flex justify-between">
					{/* 1 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Interviewed</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<p className="font-inter text-xs font-medium ">{position?.interviewedCount}</p>
						</div>
					</div>
					{/* 2 */}
					<div className="w-[50%]">
						<p className="text-[#555770] text-[10px] font-medium">Hire/Reject</p>
						<div className="flex flex-col gap-1 text-[#1F3343]">
							<p className="font-inter text-xs font-medium ">
								{position?.hiredCount}/{position?.rejectedCount}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* footer card */}
			<div className="inline-flex p-[7px_10.73px_6px_15px] justify-between items-center gap-9 rounded-b-[12px] border-t-[1px] border-t-[#EBEBF0]">
				<div className="flex flex-col">
					<div className="flex flex-col gap-2">
						<DisplayRoom offlineDriveRoom={position?.company?.offlineDriveRoom} />
						{attendDriveDate ? (
							<Tooltip
								placement="top"
								title={
									<p className="text-[#28293D] font-inter text-xs font-medium">
										<span className="text-[#1C1C28] font-inter text-[12px] leading-[16px] font-[700]">
											Registered At:{' '}
										</span>
										{attendDriveDate ? dayjs(attendDriveDate).format('Do MMM YYYY, hh:mm A') : '-'}
									</p>
								}
							>
								<p className="text-[#28293D] font-inter text-xs font-medium truncate md:truncate-none max-w-[200px]">
									{attendDriveDate ? dayjs(attendDriveDate).format('Do MMM YYYY, hh:mm A') : '-'}
								</p>
							</Tooltip>
						) : null}
					</div>
				</div>
				<div>
					<CompaniesActions
						company={position?.company}
						companyId={position?.company?._id || ''}
						companyName={position?.company?.name || ''}
						driveId={selectedDrive?.value || ''}
						positionId={position?._id?.toString() || ''}
					/>
				</div>
			</div>
		</div>
	);
};
export default MobileCardForOfflineDrivePosition;
